import { AnyAction } from '@reduxjs/toolkit';
import { IConfiguredProductData } from 'pages/ConfigurePage/ConfigureForm/hooks';
import { Dispatch } from 'react';
import { isAnswerEmpty } from 'utils/validation';
import { setExtraPriceQuestions } from 'store/Model/actions/model';
import { setSelectedConfigurationAnswers } from 'store/SubmitConfiguration/actions/submitConfiguration';
import {
  IConfigurationQuestion,
  IConfigurationQuestionOption,
  ISelectedConfigurationAnswer,
} from 'types/ConfigurationQuestion.types';
import { IQuestionDifferences } from './validation';
import { parseQuestionsObjectResponseIntoArray } from 'store/Model/reducers/model';

// Derive the initialKeys of questions BE echoed back as indicative, so we can
// seed the checkboxes when an existing configuration is loaded (cart-line edit /
// webhook edit). questionsObj is the raw keyed response object.
export const deriveIndicativeQuestionKeys = (questionsObj: object): string[] =>
  parseQuestionsObjectResponseIntoArray(questionsObj)
    .filter((question) => question.indicative)
    .map((question) => question.initialKey);

// Resolving a quotation line makes the whole line definitive in one visit, so
// the saved payload carries no indicative flags at all — including for
// questions the user left untouched. The flags stay in redux while the form is
// open, because that is what draws the highlight on the answers to resolve.
export const stripIndicativeFlags = (
  questions: object
): Record<string, object> => {
  const definitiveQuestions: Record<string, object> = {};
  for (const [questionKey, questionValue] of Object.entries(questions ?? {})) {
    const question = { ...questionValue } as Record<string, unknown>;
    delete question.indicative;
    definitiveQuestions[questionKey] = question;
  }
  return definitiveQuestions;
};

// A template may be saved with required questions left unanswered. Those still
// ride along in the query payload, and storing them would make the template
// re-apply "no answer" as if it were a deliberate choice — and send empties back
// as query input when it is applied.
//
// The payload wraps every answer in a `values` array, so an unanswered question
// arrives one level nested: `{ values: [null] }` for a scalar field and
// `{ values: [[]] }` for a multi-select or color, whose Formik value is itself an
// empty array. Both have length 1, so it is the *elements* that must be tested.
//
// Note that emptiness is not falsiness: `{ values: [false] }` (a toggle turned
// off) and `{ values: [0] }` are real answers and are kept.
export const stripEmptyAnswers = (
  questions: object
): Record<string, object> => {
  const answeredQuestions: Record<string, object> = {};
  for (const [questionKey, questionValue] of Object.entries(questions ?? {})) {
    const values = (questionValue as { values?: unknown[] })?.values;
    const isUnanswered =
      !Array.isArray(values) || !values.length || values.every(isAnswerEmpty);
    if (isUnanswered) {
      continue;
    }
    answeredQuestions[questionKey] = questionValue;
  }
  return answeredQuestions;
};

const toSelectedAnswer = (
  question: IConfigurationQuestion,
  option: IConfigurationQuestionOption
): ISelectedConfigurationAnswer => ({
  questionSlug: question.slug,
  optionSlug: option.slug,
  questionLabel: question.label,
  optionLabel: option.label,
  price: option.price,
});

const getActiveQuestions = (
  extraPriceQuestions: IConfigurationQuestion[] | null
): IConfigurationQuestion[] =>
  (extraPriceQuestions || []).filter((question) => question.is_active);

// After a successful query, refresh the active configuration questions and
// reconcile the currently selected answers: keep valid existing selections
// (re-resolved by slug so renamed labels/prices stay in sync), drop selections
// for questions/options that no longer exist, and pre-select the default option
// for any active question that has no answer yet — unless the customer has
// explicitly cleared that question (clearedSlugs), in which case the default is
// not re-applied.
export const handleConfigurationQuestionsAfterSuccessfulQuery = (
  dispatch: Dispatch<AnyAction>,
  currentAnswers: ISelectedConfigurationAnswer[],
  clearedSlugs: string[],
  extraPriceQuestions: IConfigurationQuestion[] | null
) => {
  const activeQuestions = getActiveQuestions(extraPriceQuestions);
  dispatch(setExtraPriceQuestions(activeQuestions));

  const reconciled: ISelectedConfigurationAnswer[] = [];
  for (const question of activeQuestions) {
    const existing = currentAnswers.find(
      (answer) => answer.questionSlug === question.slug
    );
    if (existing) {
      const option = question.options.find(
        (current) => current.slug === existing.optionSlug
      );
      if (option) {
        reconciled.push(toSelectedAnswer(question, option));
        continue;
      }
    }
    // Don't re-apply the default for a question the customer cleared.
    if (clearedSlugs.includes(question.slug)) {
      continue;
    }
    const defaultOption = question.options.find((option) => option.is_default);
    if (defaultOption) {
      reconciled.push(toSelectedAnswer(question, defaultOption));
    }
  }
  dispatch(setSelectedConfigurationAnswers(reconciled));
};

// Internal shape used to match a saved answer (slug-based or legacy label-based)
// against the current set of questions/options.
interface ISavedAnswerLookup {
  questionSlug?: string;
  optionSlug?: string;
  questionLabel?: string;
  optionLabel?: string;
}

const getSavedAnswersFromEditConfiguration = (
  editConfigurationData: IConfiguredProductData
): ISavedAnswerLookup[] => {
  // Preferred: new slug-based answers returned by get-edit-configuration.
  if (editConfigurationData.extra_price_questions?.length) {
    return editConfigurationData.extra_price_questions.map((answer) => ({
      questionSlug: answer.slug,
      optionSlug: answer.selected_option.slug,
      questionLabel: answer.label,
      optionLabel: answer.selected_option.label,
    }));
  }
  // Best-effort legacy read: the old single extra price question stored labels.
  if (
    editConfigurationData.extraPriceQuestionName &&
    editConfigurationData.selectedExtraPriceQuestionOption
  ) {
    return [
      {
        questionLabel: editConfigurationData.extraPriceQuestionName,
        optionLabel:
          editConfigurationData.selectedExtraPriceQuestionOption.label,
      },
    ];
  }
  return [];
};

// When re-opening an existing configuration, restore the saved answers against
// the current questions. Matches by slug first, then falls back to label for
// legacy (pre-slug) configurations. Reports differences for the validation
// warnings modal when a saved question/option no longer exists.
export const handleConfigurationQuestionsAfterObtainingEditConfiguration = (
  dispatch: Dispatch<AnyAction>,
  extraPriceQuestions: IConfigurationQuestion[] | null,
  editConfigurationData: IConfiguredProductData
): IQuestionDifferences => {
  const differences: IQuestionDifferences = {
    missingQuestionsKeys: [],
    extraQuestionsKeys: [],
    matchingQuestionsWithInvalidOutputsKeys: [],
  };

  const activeQuestions = getActiveQuestions(extraPriceQuestions);
  dispatch(setExtraPriceQuestions(activeQuestions));

  const savedAnswers = getSavedAnswersFromEditConfiguration(
    editConfigurationData
  );
  const restored: ISelectedConfigurationAnswer[] = [];

  for (const saved of savedAnswers) {
    const question =
      activeQuestions.find((current) => current.slug === saved.questionSlug) ||
      activeQuestions.find((current) => current.label === saved.questionLabel);
    if (!question) {
      differences.missingQuestionsKeys.push(
        saved.questionLabel || saved.questionSlug || ''
      );
      continue;
    }
    const option =
      question.options.find((current) => current.slug === saved.optionSlug) ||
      question.options.find((current) => current.label === saved.optionLabel);
    if (!option) {
      differences.matchingQuestionsWithInvalidOutputsKeys.push(question.label);
      continue;
    }
    restored.push(toSelectedAnswer(question, option));
  }

  // Pre-select defaults for any active question that wasn't part of the saved answers.
  for (const question of activeQuestions) {
    if (restored.some((answer) => answer.questionSlug === question.slug)) {
      continue;
    }
    const defaultOption = question.options.find((option) => option.is_default);
    if (defaultOption) {
      restored.push(toSelectedAnswer(question, defaultOption));
    }
  }

  dispatch(setSelectedConfigurationAnswers(restored));
  return differences;
};

export const mergeDifferences = (
  differences: IQuestionDifferences,
  extraPriceQuestionDifferences: IQuestionDifferences
) => {
  differences.missingQuestionsKeys = [
    ...differences.missingQuestionsKeys,
    ...extraPriceQuestionDifferences.missingQuestionsKeys,
  ];
  differences.extraQuestionsKeys = [
    ...differences.extraQuestionsKeys,
    ...extraPriceQuestionDifferences.extraQuestionsKeys,
  ];
  differences.matchingQuestionsWithInvalidOutputsKeys = [
    ...differences.matchingQuestionsWithInvalidOutputsKeys,
    ...extraPriceQuestionDifferences.matchingQuestionsWithInvalidOutputsKeys,
  ];
  return differences;
};

// Remove everything except custom_discount and custom_amount from pricing object
export const cleanPricingQueryData = (pricing?: Record<string, any>) => {
  if (!pricing) return undefined;

  const cleanedPricing: Record<string, any> = {};

  Object.entries(pricing).forEach(([key, value]) => {
    if (value && typeof value === 'object') {
      const { custom_discount = null, custom_amount = null } = value;
      cleanedPricing[key] = { custom_discount, custom_amount };
    }
  });

  return cleanedPricing;
};

export const havePricingValuesChanged = (
  prevPricing: Record<string, any> | null,
  currentPricing: Record<string, any> | null
): boolean => {
  if (!prevPricing || !currentPricing) return true;

  const allKeysArray = Array.from(
    new Set([...Object.keys(prevPricing), ...Object.keys(currentPricing)])
  );

  for (const key of allKeysArray) {
    const prevAmount = prevPricing[key]?.custom_amount;
    const currentAmount = currentPricing[key]?.custom_amount;

    const prevDiscount = prevPricing[key]?.custom_discount;
    const currentDiscount = currentPricing[key]?.custom_discount;

    if (prevAmount !== currentAmount || prevDiscount !== currentDiscount) {
      return true;
    }
  }

  return false;
};
